import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Receipt, Plus, Check, Trash2, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// Dollar total = rupee total / this rate (same ~₹88 per $1 as SYSTEM-GUIDE costs).
const USD_TO_INR = 88;

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const usd = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseIso = (s) => new Date(`${s}T00:00:00`);
const fmtDate = (s) => parseIso(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const REPEATS = [["1", "Every month"], ["12", "Every year"], ["36", "Every 3 years"], ["0", "One time"]];

function dueText(s) {
  if (s.done) return { text: "Paid", cls: "text-slate-500" };
  if (s.days_left < 0) return { text: `${-s.days_left} day${s.days_left === -1 ? "" : "s"} overdue`, cls: "text-red-500 font-bold" };
  if (s.days_left === 0) return { text: "Due today", cls: "text-red-500 font-bold" };
  if (s.days_left === 1) return { text: "Due tomorrow", cls: "text-red-500 font-bold" };
  return { text: `In ${s.days_left} days`, cls: "text-slate-400" };
}

function DateField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full flex items-center justify-between gap-2 border border-white/20 bg-white/5 text-white rounded-md px-3 py-2 text-sm hover:bg-white/10">
          {fmtDate(value)} <CalendarIcon className="h-4 w-4 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" weekStartsOn={1} defaultMonth={parseIso(value)} selected={parseIso(value)}
          onSelect={(d) => { if (d) { onChange(iso(d)); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}

const EMPTY = { name: "", purpose: "", next_due: iso(new Date()), usd: "", inr: "", every_months: "1" };

export default function BillTracking() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/billing-services");
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = () => window.dispatchEvent(new Event("billing-updated"));

  const markPaid = async (s) => {
    setBusy(s.id);
    try {
      await api.post(`/billing-services/${s.id}/paid`);
      toast.success(`${s.name} marked paid`);
      await load();
      changed();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}" from Bill Tracking?`)) return;
    setBusy(s.id);
    try {
      await api.delete(`/billing-services/${s.id}`);
      await load();
      changed();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/billing-services", {
        name: form.name,
        purpose: form.purpose,
        next_due: form.next_due,
        usd: parseFloat(form.usd) || 0,
        inr: parseFloat(form.inr) || 0,
        every_months: parseInt(form.every_months, 10),
      });
      toast.success("Added");
      setOpen(false);
      setForm(EMPTY);
      await load();
      changed();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const totalInr = useMemo(() => items.filter((s) => !s.done).reduce((a, s) => a + s.monthly_inr, 0), [items]);
  const totalUsd = totalInr / USD_TO_INR;

  const label = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";
  const card = "bg-[#141414] border border-white/10 rounded-lg p-4";

  return (
    <div data-testid="bill-tracking-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Bill Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">Our own services and subscriptions. A red alert shows on the bell 1 day before each due date.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-tracker"
          className="bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-[0.97] transition-all"
        >
          <Plus className="h-4 w-4" /> Add tracker
        </button>
      </div>

      {loading ? (
        <p className="mt-8 text-slate-500 text-sm">Loading…</p>
      ) : (
        <div className={`${card} mt-6 max-w-6xl`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-xs">
                  <th className="py-2 pr-4 font-semibold">Service</th>
                  <th className="py-2 pr-4 font-semibold">What it's for</th>
                  <th className="py-2 pr-4 font-semibold">Date</th>
                  <th className="py-2 pr-4 font-semibold text-right">Dollar</th>
                  <th className="py-2 pr-4 font-semibold text-right">Rupee / month</th>
                  <th className="py-2 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const d = dueText(s);
                  return (
                    <tr key={s.id} className={`border-t border-white/10 text-slate-300 ${s.alert ? "bg-red-500/5" : ""}`}>
                      <td className="py-2.5 pr-4 font-bold text-white">
                        {s.name}
                        {s.every_months !== 1 && (
                          <span className="block text-[10px] font-normal text-slate-500">
                            {s.every_months === 0 ? "one time" : `${inr(s.inr)} every ${s.every_months} months`}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-slate-400">{s.purpose}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        {fmtDate(s.next_due)}
                        <span className={`block text-xs ${d.cls}`}>{d.text}</span>
                      </td>
                      <td className="py-2.5 pr-4 text-right">{s.usd ? usd(s.usd) : "none"}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-white">{inr(s.every_months === 0 ? s.inr : s.monthly_inr)}</td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => markPaid(s)}
                          disabled={busy === s.id || s.done}
                          data-testid={`paid-${s.id}`}
                          className="bg-green-600 hover:bg-green-500 disabled:opacity-40 text-white font-semibold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1 active:scale-[0.97] transition-all"
                        >
                          {busy === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Paid
                        </button>
                        <button
                          onClick={() => remove(s)}
                          disabled={busy === s.id}
                          title="Remove"
                          className="ml-2 text-slate-600 hover:text-red-500 align-middle"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t border-white/20 text-white font-bold">
                  <td className="py-3 pr-4" colSpan={3}>TOTAL per month</td>
                  <td className="py-3 pr-4 text-right text-orange-500">≈ {usd(totalUsd)}</td>
                  <td className="py-3 pr-4 text-right text-orange-500">{inr(totalInr)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Yearly and 3-year bills are spread per month (e.g. GoDaddy). Dollar total uses ₹{USD_TO_INR} = $1. Railway is $5 minimum + usage.
          </p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Add tracker</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={label}>Service name</label>
              <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Domain, Hosting" />
            </div>
            <div>
              <label className={label}>What it's for</label>
              <Input className="mt-1" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Short note" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Next due date</label>
                <div className="mt-1"><DateField value={form.next_due} onChange={(v) => setForm({ ...form, next_due: v })} /></div>
              </div>
              <div>
                <label className={label}>Repeats</label>
                <Select value={form.every_months} onValueChange={(v) => setForm({ ...form, every_months: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPEATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Dollar ($)</label>
                <Input className="mt-1" type="number" min="0" step="0.01" value={form.usd} onChange={(e) => setForm({ ...form, usd: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label className={label}>Rupee (₹) per bill</label>
                <Input className="mt-1" type="number" min="0" step="0.01" value={form.inr} onChange={(e) => setForm({ ...form, inr: e.target.value })} placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 text-sm">Cancel</button>
            <button
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Add
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
